// frontend/src/pages/players/PlayerDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import playerService from '../../services/playerservice';
import championshipService from '../../services/championshipService';
import teamService from '../../services/teamService'; 
import './PlayerDetail.css';

// Componente filho para informações pessoais
function PlayerInfoGrid({ player }) {
  const formatDate = (dateString) => {
    if (!dateString) return 'Não informada';
    try {
      const dateParts = String(dateString).split('T')[0].split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const day = parseInt(dateParts[2], 10);
        return new Date(year, month, day).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) {
      return 'Inválida';
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    try {
      const dateParts = String(birthDate).split('T')[0].split('-');
      let birth;
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const day = parseInt(dateParts[2], 10);
        birth = new Date(year, month, day);
      } else {
        birth = new Date(birthDate);
      }
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      return age >= 0 ? `${age} anos` : '';
    } catch (e) {
      return '';
    }
  };

  // ✅✅✅ INÍCIO DA ALTERAÇÃO ✅✅✅
  // Função para formatar o número de telefone para exibição
  const formatPhone = (phoneString) => {
    if (!phoneString) return 'Não informado';
    const cleaned = ('' + phoneString).replace(/\D/g, '');
    if (cleaned.length === 11) {
      const match = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
      if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
      }
    }
    if (cleaned.length === 10) {
      const match = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
      if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
      }
    }
    return phoneString; // Retorna o original se não conseguir formatar
  };
  // ✅✅✅ FIM DA ALTERAÇÃO ✅✅✅

  return (
    <div className="info-grid">
      <div className="info-item">
        <span className="info-label">Posição:</span>
        <span className="info-value">{player.position || 'Não informada'}</span>
      </div>
      <div className="info-item">
        <span className="info-label">Data de Nasc.:</span>
        <span className="info-value">{formatDate(player.birthDate)} {player.birthDate && `(${calculateAge(player.birthDate)})`}</span>
      </div>
      {/* ✅✅✅ INÍCIO DA ALTERAÇÃO ✅✅✅ */}
      <div className="info-item">
        <span className="info-label">Telefone:</span>
        <span className="info-value">{formatPhone(player.phone)}</span>
      </div>
      {/* ✅✅✅ FIM DA ALTERAÇÃO ✅✅✅ */}
    </div>
  );
}

// ... O restante do arquivo PlayerDetail.jsx continua o mesmo ...
function PlayerDetail() {
  const { playerId } = useParams();
  const navigate = useNavigate();

  const [player, setPlayer] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [playerImgSrc, setPlayerImgSrc] = useState('/default-player-photo.png');
  const [currentChampionshipName, setCurrentChampionshipName] = useState('');

  const getTeamLogoUrlFromService = (badgeFilename) => {
    if (!badgeFilename) return '/default-team-badge.png';
    if (typeof teamService?.getTeamLogoUrl === 'function') {
      return teamService.getTeamLogoUrl(badgeFilename);
    }
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '');
    return `${baseUrl}/uploads/${badgeFilename}`; 
  };

  useEffect(() => {
    const fetchPlayerDataAndChampionship = async () => {
      if (!playerId) { setError("ID do jogador não fornecido."); setIsLoading(false); return; }
      
      setIsLoading(true); setError(''); setCurrentChampionshipName(''); setPlayer(null); 

      try {
        const response = await playerService.getPlayerById(playerId);
        const playerData = response?.data;

        if (playerData && typeof playerData === 'object' && Object.keys(playerData).length > 0) {
          setPlayer(playerData);

          if (playerData.photo) {
            setPlayerImgSrc(playerService.getPlayerPhotoUrl(playerData.photo)); 
          } else {
            setPlayerImgSrc('/default-player-photo.png');
          }
          
          let championshipIdToFetch = null;
          let potentialChampionshipName = "";

          if (playerData.currentChampionship?._id) { 
            championshipIdToFetch = playerData.currentChampionship._id; 
            potentialChampionshipName = playerData.currentChampionship.name || ""; 
          } else if (playerData.currentTeam?.currentChampionshipId) { 
            championshipIdToFetch = playerData.currentTeam.currentChampionshipId; 
            potentialChampionshipName = playerData.currentTeam.currentChampionshipName || playerData.currentTeam.championship?.name || ""; 
          } else if (playerData.currentTeam?.championships?.[0]?._id) { 
            championshipIdToFetch = playerData.currentTeam.championships[0]._id; 
            potentialChampionshipName = playerData.currentTeam.championships[0].name || ""; 
          } else if (playerData.championships?.[0]?._id) { 
            championshipIdToFetch = playerData.championships[0]._id; 
            potentialChampionshipName = playerData.championships[0].name || ""; 
          }

          if (championshipIdToFetch) {
            if (potentialChampionshipName?.trim()) { 
              setCurrentChampionshipName(potentialChampionshipName.toUpperCase());
            } else { 
                try {
                    const champResponse = await championshipService.getChampionshipDetails(championshipIdToFetch);
                    const champData = champResponse?.data;
                    if (champData?.name) { setCurrentChampionshipName(champData.name.toUpperCase()); }
                } catch (champError) { console.error("Erro ao buscar camp. do jogador:", champError); }
            }
          }
        } else {
          setError('Jogador não encontrado ou dados inválidos.');
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Falha ao carregar dados.');
      } finally {
        setIsLoading(false);
      }
    };

    if (playerId) fetchPlayerDataAndChampionship();
    else { setError("ID do jogador ausente."); setIsLoading(false); }

  }, [playerId]); 

  const handlePlayerImageError = () => setPlayerImgSrc('/default-player-photo.png');

  const totalGoals = Array.isArray(player?.goals) ? player.goals.length : (player?.goals || 0);
  const yellowCards = Array.isArray(player?.cards) 
    ? player.cards.filter(c => c.type === 'yellow').length 
    : (player?.yellowCards || 0);
  const redCards = Array.isArray(player?.cards) 
    ? player.cards.filter(c => c.type === 'red' || c.type === 'second_yellow').length 
    : (player?.redCards || 0);

  if (isLoading) return (<div className="loading-container"><p className="loading-text">Carregando dados do jogador...</p></div>);
  if (error) return (<div className="player-detail-container error-page"><h2 className="page-title error-title">Erro</h2><p className="error-message-text">{error}</p><button onClick={() => navigate(-1)} className="button button-primary">Voltar</button></div>);
  if (!player) return (<div className="player-detail-container not-found-page"><h2 className="page-title">Jogador não encontrado</h2><p>Detalhes indisponíveis.</p><button onClick={() => navigate(-1)} className="button button-primary">Voltar</button></div>);

  return (
    <div className="player-detail-page-container">
      <h2 className="player-detail-page-title">Detalhes do Jogador</h2>
      
      <div className="player-detail-content-card">
        
        <section className="player-header-section">
          <div className="player-photo-wrapper">
            <img src={playerImgSrc} alt={`Foto de ${player.name || 'jogador'}`} className="player-main-photo" onError={handlePlayerImageError}/>
          </div>
          <div className="player-main-identity">
            <h1 className="player-main-name">{player.name || 'Nome não disponível'}</h1>
            <p className="player-main-meta">
              {player.position || 'Posição não informada'}
              {player.shirtNumber && ` • Camisa #${player.shirtNumber}`}
            </p>
            {currentChampionshipName && ( <p className="player-current-championship"> Campeonato: <strong>{currentChampionshipName}</strong></p> )}
          </div>
        </section>

        <section className="player-info-section">
          <h3 className="section-title">Informações Pessoais</h3>
          <PlayerInfoGrid player={player} />
        </section>

        <section className="player-content-section player-summary-stats-section">
            <h3 className="section-title">Estatísticas Resumidas</h3>
            <div className="summary-stats-grid">
                <div className="info-item">
                    <span className="info-label">Gols Marcados:</span>
                    <span className="info-value">{totalGoals}</span>
                </div>
                <div className="info-item">
                    <span className="info-label">Cartões Amarelos:</span>
                    <span className="info-value">{yellowCards}</span>
                </div>
                <div className="info-item">
                    <span className="info-label">Cartões Vermelhos:</span>
                    <span className="info-value">{redCards}</span>
                </div>
            </div>
        </section>

        {(player.currentTeam?.name || (player.teams && player.teams.length > 0)) && (
           <section className="player-content-section player-teams-list-section">
                <h3 className="section-title">Histórico de Clubes</h3>
                { (player.currentTeam || player.teams?.length > 0) ? (
                    <ul className="clubs-list">
                        {player.currentTeam?.name && (
                            <li className="club-item current-club" key={player.currentTeam._id || 'current-team'}>
                                <img src={getTeamLogoUrlFromService(player.currentTeam.badge)} alt="" className="club-badge-small" onError={(e) => { e.target.onerror = null; e.target.src="/default-team-badge.png"}} />
                                <Link to={`/teams/${player.currentTeam._id}`} className="text-link club-name">{player.currentTeam.name}</Link>
                                <span className="club-status">(Time Atual)</span>
                                {player.currentTeam.shirtNumber && player.currentTeam.shirtNumber !== player.shirtNumber && <span className="club-meta"> • Camisa #{player.currentTeam.shirtNumber}</span>}
                            </li>
                        )}
                        {player.teams?.filter(team => team && team.name && (!player.currentTeam || team._id !== player.currentTeam._id))
                                      .map(team => (
                            <li key={team._id} className="club-item">
                                 <img src={getTeamLogoUrlFromService(team.badge)} alt="" className="club-badge-small" onError={(e) => { e.target.onerror = null; e.target.src="/default-team-badge.png"}}/>
                                <Link to={`/teams/${team._id}`} className="text-link club-name">{team.name}</Link>
                            </li>
                        ))}
                    </ul>
                ) : (<p className="empty-list-message">Nenhum clube registrado.</p>)}
            </section>
        )}

        <div className="action-buttons-footer">
          <button onClick={() => navigate(-1)} className="button button-secondary">Voltar</button>
          <button onClick={() => navigate(`/players/${playerId}/edit`)} className="button button-primary">Editar Jogador</button>
          <button onClick={() => navigate(`/players/${playerId}/id-card`)} className="button button-info">Gerar Carteirinha</button>
        </div>
      </div>
    </div>
  );
}

export default PlayerDetail;